import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchExchangeRates } from '../store/currencySlice';
import { setLanguage } from '../store/languageSlice';
import { setCredentials, logout, sessionRestoreComplete } from '../store/authSlice';
import { clearAuthStorage, getAccessToken } from '../utils/authStorage';
import { useTranslation } from 'react-i18next';

let sessionBootstrapPromise = null;

export const shouldRestorePersistedSession = () =>
  process.env.REACT_APP_RESTORE_SESSION !== 'false';

export function restoreSessionOnce(api) {
  if (!sessionBootstrapPromise) {
    const token = getAccessToken();
    if (!token) return Promise.resolve(null);
    sessionBootstrapPromise = fetch(`${api}/api/v2/auth/me`, { headers: { Authorization: `Bearer ${token}` }, credentials: 'omit' })
      .then(async identity => {
        if ([401, 403].includes(identity.status)) return null;
        if (!identity.ok) throw new Error('SESSION_RESTORE_UNAVAILABLE');
        const user = await identity.json();
        const userId = user?.id || user?.sub;
        if (!userId || !['USER', 'PARTNER', 'ADMIN', 'FINANCE'].includes(user?.role)) {
          throw new Error('INVALID_SESSION_RESPONSE');
        }
        return { user, access_token: token };
      })
      .finally(() => {
        sessionBootstrapPromise = null;
      });
  }
  return sessionBootstrapPromise;
}

/**
 * AppInitializer - Runs once on app mount to:
 * 1. Fetch live exchange rates
 * 2. Sync Redux language state with i18n
 * 3. Refresh rates every 24 hours
 */
const AppInitializer = ({ children }) => {
  const dispatch = useDispatch();
  const { i18n } = useTranslation();
  const savedLanguage = useSelector((state) => state.language.selectedLanguage);
  const isSessionRestored = useSelector((state) => state.auth.isSessionRestored);
  const [restoreAttempt, setRestoreAttempt] = useState(0);
  const [restoreError, setRestoreError] = useState(false);

  useEffect(() => {
    let active = true;
    const api = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8020';
    setRestoreError(false);
    if (!shouldRestorePersistedSession()) {
      clearAuthStorage();
      dispatch(logout());
      dispatch(sessionRestoreComplete());
      return () => { active = false; };
    }
    restoreSessionOnce(api)
      .then((session) => {
        if (!active) return;
        if (session) {
          dispatch(setCredentials(session));
        } else {
          clearAuthStorage();
          dispatch(logout());
        }
      })
      .catch(() => {
        if (active) {
          // A network/5xx failure does not prove the persisted session is invalid.
          setRestoreError(true);
        }
      });
    return () => { active = false; };
  }, [dispatch, restoreAttempt]);

  useEffect(() => {
    // Sync i18n with saved language
    if (i18n.language !== savedLanguage) {
      i18n.changeLanguage(savedLanguage);
    }
  }, [i18n, savedLanguage]);

  useEffect(() => {
    if (!isSessionRestored) return undefined;
    // Fetch exchange rates on mount
    dispatch(fetchExchangeRates());

    // Refresh rates every 24 hours
    const TWENTY_FOUR_HOURS = 24 * 60 * 60 * 1000;
    const interval = setInterval(() => dispatch(fetchExchangeRates()), TWENTY_FOUR_HOURS);

    return () => clearInterval(interval);
  }, [dispatch, isSessionRestored]);

  if (!isSessionRestored) {
    return <div className="page-loader" role={restoreError ? 'alert' : 'status'}>
      <div className="page-loader-spinner" />
      <p>{restoreError ? 'We could not verify your session.' : 'Restoring your session…'}</p>
      {restoreError && <button type="button" onClick={() => setRestoreAttempt(value => value + 1)}>Retry</button>}
    </div>;
  }
  return children;
};

export default AppInitializer;
